// MemoCard.jsx – Fotokarte und Namenskarte wie im Druck
// Masse aus den Vorlagen vermessen (in % der Kartenbreite):
//   Rand 9,4 %, Eckenradius 8,3 %, Foto 72,7 % (links 13,5 %, oben 13,9 %),
//   Name vorne: Grossbuchstaben 3 %, Namenskarte: 9,2 % / Datum 5,1 %
import React from 'react';
import { SymbolShape } from './PersonCard';
import { splitName, cardDate } from '../utils/photoEdit';
import { ORNAMENT_MASK_URL } from '../utils/memoOrnament';
import './MemoCard.css';
import { SmallCaps } from '../utils/smallCaps';

// Rahmenfarbe, wenn eine Person (noch) keine Familienfarbe hat.
const GENDER_COLORS = { male: '#5A9ED6', female: '#D4787A' };

// Rahmenfarbe(n) als CSS-Hintergrund
// Gleiche Aufteilung wie frameSegments() in utils/cardRender.js – hier aber als
// CSS-„conic-gradient“ (Farbverlauf im Kreis), damit Vorschau und Druck gleich aussehen.
export function frameBackground(person, colorInfo) {
  const cols = colorInfo?.borderColors;
  if (cols && cols.length) {
    const n = cols.length;
    if (n === 1) return cols[0];
    if (n === 2) return `conic-gradient(${cols[1]} 0deg 90deg, ${cols[0]} 90deg 270deg, ${cols[1]} 270deg 360deg)`;
    if (n === 3) {
      // wie Vorlage: oben rechts / unten / oben links
      return `conic-gradient(${cols[1]} 0deg 98deg, ${cols[0]} 98deg 262deg, ${cols[2]} 262deg 360deg)`;
    }
    // ab 4 Farben: gleich grosse Abschnitte, erste Farbe unten
    const seg = 360 / n;
    const start = 180 - seg / 2;
    const parts = cols.map((c, i) => `${c} ${i * seg}deg ${(i + 1) * seg}deg`).join(', ');
    return `conic-gradient(from ${start}deg, ${parts})`;
  }
  if (colorInfo?.color) return colorInfo.color;
  return GENDER_COLORS[person.gender] || '#8B8B8B';
}

// Symbole unten rechts in der weissen Fläche (Umriss-Symbol zuerst, dann gefüllte).
function CardSymbols({ colorInfo }) {
  const syms = [];
  if (colorInfo?.outlineSymbol) syms.push({ type: colorInfo.outlineSymbol, outline: true });
  (colorInfo?.symbols || []).forEach(t => syms.push({ type: t, outline: false }));
  if (!syms.length) return null;
  const color = colorInfo?.color || '#555';
  return (
    <div className="mc-symbols">
      {syms.map((s, i) => (
        <svg key={i} viewBox="0 0 12 12" className="mc-symbol">
          {s.outline
            ? <SymbolShape type={s.type} color="none" stroke={color} strokeWidth={1.4} size={12} />
            : <SymbolShape type={s.type} color={color} stroke="#333" strokeWidth={0.4} size={12} />}
        </svg>
      ))}
    </div>
  );
}

// blank: leere Karte mit Zierrahmen (Leerperson), ohne Namen
// Wasserzeichen (Kopierschutz in der Vorschau)
const WATERMARK = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="220" height="140"><text x="110" y="78" text-anchor="middle" transform="rotate(-28 110 70)" font-family="Arial, sans-serif" font-size="30" font-weight="700" fill="rgba(0,0,0,0.16)" stroke="rgba(255,255,255,0.35)" stroke-width="1">BandeleArt</text></svg>'
)}")`;

// Eine Memo-Karte für die Vorschau (HTML/CSS statt Canvas). Props:
//   person, colorInfo – Person und ihre Farben/Symbole (aus computeColorSystem)
//   side        – 'front' = Fotokarte, 'back' = Namenskarte
//   size        – Kantenlänge in Pixeln; alle Masse in MemoCard.css sind relativ dazu (em)
//   photoUrl    – verkleinertes Foto für die Vorschau; photoPending = lädt noch
//   onPhotoClick – Klick aufs Foto (Bild bearbeiten / Foto hinzufügen), nur wenn erlaubt
//   watermark   – Schriftzug „BandeleArt“ als Kopierschutz
export function MemoCard({ person, colorInfo, side = 'front', size = 300, photoUrl, photoPending = false, onPhotoClick, watermark = true, blank = false }) {
  const { first, last } = splitName(person);
  const frame = frameBackground(person, colorInfo);
  // fontSize = size: Dadurch entspricht 1em in der CSS-Datei genau der Kartenbreite.
  const style = { width: size, height: size, fontSize: size, background: frame };
  // Ohne Foto wird der Zierrahmen angezeigt.
  const ornament = side === 'front' && !photoUrl && !photoPending; // Foto lädt noch → kein Zierrahmen aufblitzen lassen
  return (
    // Rechtsklick (Bild speichern) unterbinden.
    <div className="mc-card" style={style} onContextMenu={e => e.preventDefault()}>
      <div className="mc-inner">
        {side === 'front' ? (
          <>
            <div
              className={`mc-photo${onPhotoClick ? ' mc-photo--click' : ''}${photoUrl || photoPending ? '' : ' mc-photo--none'}`}
              onClick={onPhotoClick}
              title={onPhotoClick ? (photoUrl ? 'Bild bearbeiten' : 'Foto hinzufügen') : undefined}
            >
              {photoUrl && <img src={photoUrl} alt="" draggable={false} />}
              {photoUrl && onPhotoClick && <div className="mc-photo-edit">✎ Bild bearbeiten</div>}
              {ornament && onPhotoClick && <div className="mc-photo-add">+ Foto hinzufügen</div>}
            </div>
            {!blank && <div className="mc-front-name mc-hand"><SmallCaps text={first || '(Vorname)'} /></div>}
          </>
        ) : (
          <div className="mc-back mc-hand">
            <div className="mc-back-first"><SmallCaps text={first || '(Vorname)'} /></div>
            {last && <div className="mc-back-last"><SmallCaps text={last} /></div>}
            {person.maidenName && <div className="mc-back-maiden"><SmallCaps text={`(geb. ${person.maidenName})`} /></div>}
            {person.birthDate && <div className={`mc-back-date${person.maidenName ? ' mc-back-date--after-maiden' : ''}`}><SmallCaps text={cardDate(person.birthDate)} /></div>}
          </div>
        )}
        <CardSymbols colorInfo={colorInfo} />
      </div>
      {/* ohne Foto: Zierrahmen in den Farben des äusseren Rahmens (wie im Druck) */}
      {ornament && (
        <div className="mc-ornament" style={{ background: frame, WebkitMaskImage: ORNAMENT_MASK_URL, maskImage: ORNAMENT_MASK_URL }} />
      )}
      {watermark && <div className="mc-watermark" style={{ backgroundImage: WATERMARK }} />}
    </div>
  );
}
